import { useCallback } from "react";
import type { Node } from "@xyflow/react";
import { useChainStore } from "../store/chainStore";
import { useLayoutStore, type XY } from "../store/layoutStore";
import { computeElkLayout } from "./elkLayout";
import { useLayoutSaver } from "./useLayoutSaver";

export function useAutoArrange(rfNodes: Node[]): () => void {
  const systems = useChainStore((s) => s.systems);
  const connections = useChainStore((s) => s.connections);
  const setPositions = useLayoutStore((s) => s.setPositions);
  const resetLayout = useLayoutStore((s) => s.resetLayout);
  const { flushNow } = useLayoutSaver();

  return useCallback(() => {
    if (systems.length === 0) return;
    const selectedIds = new Set(
      rfNodes.filter((n) => n.selected).map((n) => n.id),
    );
    if (selectedIds.size > 1) {
      const subset = systems.filter((s) => selectedIds.has(s.id));
      const subsetConnections = connections.filter(
        (c) => selectedIds.has(c.from) && selectedIds.has(c.to),
      );
      const active = useLayoutStore.getState().positions;
      const origin = subset.reduce(
        (acc, s) => {
          const p = active[s.id] ?? { x: 0, y: 0 };
          return { x: Math.min(acc.x, p.x), y: Math.min(acc.y, p.y) };
        },
        { x: Infinity, y: Infinity },
      );
      computeElkLayout(subset, subsetConnections).then((elkPositions) => {
        const elkOrigin = Object.values(elkPositions).reduce(
          (acc, p) => ({ x: Math.min(acc.x, p.x), y: Math.min(acc.y, p.y) }),
          { x: Infinity, y: Infinity },
        );
        const dx = origin.x - elkOrigin.x;
        const dy = origin.y - elkOrigin.y;
        const updates: Record<string, XY> = {};
        for (const [id, p] of Object.entries(elkPositions)) {
          updates[id] = { x: p.x + dx, y: p.y + dy };
        }
        setPositions(updates);
        flushNow(updates);
      });
      return;
    }
    computeElkLayout(systems, connections).then((positions) => {
      resetLayout(positions);
      flushNow(positions);
    });
  }, [systems, connections, rfNodes, setPositions, resetLayout, flushNow]);
}
